import {
  BaseBreadcrumb,
  Button,
  Icon,
  IconColor,
  InlineTextEdit,
  ShareDialog,
  Tooltip,
  useAuth,
  useGetDocumentById,
  useGetSharedDocumentInfo,
  useRenameDocument,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import {
  useSearchParams,
  useParams,
  usePathname,
  useRouter,
} from 'next/navigation'
import { useGetDocument } from '@/dc-shared-web/src/hooks/api/document/useGetDocument'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import {
  faArrowRightArrowLeft,
  faShare as faShareRegular,
} from '@document-crunch/pro-regular-svg-icons'
import DownloadDialog from '@/app/crunches/[id]/Subheader/DownloadDialog'
import { usePdfInstance } from '@/app/crunches/[id]/PdfInstanceContext'
import { useFetchComparisonList } from '@/dc-shared-web/src/hooks/api/comparison'
import {
  COMPARISON_SEARCH_PARAM,
  INFO_PANEL_SEARCH_PARAM,
  VERSION_SEARCH_PARAM,
  SHOW_VERSION_UPLOAD_SEARCH_PARAM,
  AUTO_PICK_FILE_SEARCH_PARAM,
} from '@/app/crunches/[id]/constants'
import { isVersionTwoTabSelected } from '@/app/crunches/[id]/InfoPanel/Version/utils'

import { useCanUploadVersion } from '@/app/core/hooks/version/useCanUploadVersion'
import { FS, FS_CATEGORY } from '@/dc-shared-web/src/lib/trackingConstants'

export default function InnerHeader() {
  const { pspdfkitInstance } = usePdfInstance()
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const searchParams = useSearchParams()
  const infoPanelSearchParam =
    searchParams.get(INFO_PANEL_SEARCH_PARAM) ?? 'Home'
  const selectedComparisonId = searchParams.get(COMPARISON_SEARCH_PARAM)

  const {
    data: crunchDataSvcCrunches,
    isLoading: isLoadingCrunchDataSvcCrunches,
  } = useGetDocument(id)
  const { data: crunchData, isLoading: isLoadingCrunchData } =
    useGetDocumentById(id)
  const { data: shareInfo } = useGetSharedDocumentInfo({
    uuid: crunchData?.id,
  })
  const { data: comparisonList } = useFetchComparisonList(
    crunchData?.id ?? '',
    infoPanelSearchParam === 'Version'
  )
  const canUploadVersion = useCanUploadVersion({
    crunchDataSvcCrunches,
    user,
    crunchData,
    comparisonList,
  })

  const [editedName, setEditedName] = useState(crunchData?.label)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)

  useEffect(() => {
    setEditedName(crunchData?.label)
  }, [crunchData?.label])

  const currentTeamUuid = crunchData?.teamUuid
  const breadcrumbParams = new URLSearchParams(searchParams)
  if (currentTeamUuid) {
    breadcrumbParams.set('selectedTeamId', currentTeamUuid)
  }

  const { mutate: renameCrunch, isLoading } = useRenameDocument({
    documentId: id,
  })

  const dashBreadcrumbItem = {
    label: 'Dashboard',
    url: `/dash?${breadcrumbParams.toString()}`,
  }
  const breadcrumbItems =
    crunchDataSvcCrunches?.parent
      ?.map((breadcrumb) => ({
        label: breadcrumb.name,
        url: `/${breadcrumb.type}s/${breadcrumb.uuid}?${breadcrumbParams.toString()}`,
      }))
      .reverse() || []

  const handleNameChange = (name: string) => {
    if (name === crunchData?.label) return
    if (name === '') {
      setEditedName(crunchData?.label)
      return
    }
    setEditedName(name)

    renameCrunch({
      uuid: id,
      label: name,
      oldName: crunchData?.label || '',
    })
  }

  const isVersionCompV2Selected = isVersionTwoTabSelected({
    comparisonList,
    comparisonUuid: selectedComparisonId,
    versionUuid: searchParams.get(VERSION_SEARCH_PARAM),
  })

  if (isLoadingCrunchData || isLoadingCrunchDataSvcCrunches) {
    return null
  }

  return (
    <>
      {crunchData && crunchDataSvcCrunches && (
        <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-2'>
          <div className='tw-flex tw-items-center tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-max-w-[calc(100vw-400px)] lg:tw-max-w-[calc(100vw-520px)]'>
            <div className='tw-hidden sm:tw-block tw-min-w-0 tw-flex-shrink'>
              <BaseBreadcrumb
                LinkComponent={Link}
                items={[dashBreadcrumbItem, ...breadcrumbItems]}
                showSeparatorAfterLastItem
              />
            </div>
            <div className='tw-min-w-fit tw-flex-1 tw-ml-2 tw-overflow-hidden tw-w-fit tw-flex-shrink-0 tw-max-w-full'>
              <InlineTextEdit
                datatestid='crunch-name'
                value={editedName || crunchData?.label || ''}
                className='tw-w-auto tw-px-2 tw-text-sm tw-flex tw-items-center tw-font-medium tw-truncate tw-max-w-full'
                inputClassName='tw-w-full tw-py-2 tw-h-6 tw-text-sm tw-truncate tw-max-w-full'
                savedClassName='tw-w-fit tw-block'
                onValueChange={handleNameChange}
                required
                placeholder='Add crunch name'
                canEdit={
                  !isLoading &&
                  !user?._isUnaffiliatedUser &&
                  !crunchData?.meta.isShared
                }
              />
            </div>
          </div>
          <div className='tw-flex tw-items-center tw-flex-shrink-0 tw-gap-2'>
            {canUploadVersion && <UploadNewVersionButton id={crunchData?.id} />}
            {!user?._isUnaffiliatedUser && !crunchData?.meta.isShared && (
              <Tooltip content={'Share'} className='lg:tw-hidden' withArrow>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => setShareDialogOpen(true)}
                  data-testid='share-modal-open-btn'
                  className='tw-flex-shrink-0'
                >
                  <Icon
                    icon={faShareRegular}
                    size='1x'
                    color={IconColor.darkPrimary}
                  />
                  <span className='tw-ml-2 tw-hidden lg:tw-block tw-font-medium tw-text-sm'>
                    Share{' '}
                    {shareInfo && shareInfo?._activeShareCount > 0
                      ? `(${shareInfo?._activeShareCount})`
                      : ''}
                  </span>
                </Button>
              </Tooltip>
            )}

            {crunchData && (
              <div className='tw-flex-shrink-0'>
                <DownloadDialog
                  pspdfkitInstance={pspdfkitInstance}
                  crunchData={crunchData}
                  isVersionCompV2Selected={isVersionCompV2Selected}
                />
              </div>
            )}
          </div>

          {shareDialogOpen && crunchData?.teamUuid && (
            <ShareDialog
              open={shareDialogOpen}
              setOpen={setShareDialogOpen}
              document={{
                id: crunchData?.id,
                name: crunchData?.label,
                team: crunchData?.team,
                teamId: crunchData?.teamUuid,
              }}
              onActionSuccess={() => setShareDialogOpen(false)}
            />
          )}
        </div>
      )}
    </>
  )
}

const UploadNewVersionButton: React.FC<{ id: string }> = ({ id }) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { trackEvent } = useFSTrackEvent()

  const handleUploadNewVersionClick = () => {
    trackEvent(FS.BUTTON_CLICKED, {
      name: 'Compare versions',
      page_category: FS_CATEGORY.CRUNCH_VIEW,
      entity_id: id,
      entity_type: 'crunch',
    })

    const newSearchParams = new URLSearchParams(searchParams.toString())
    newSearchParams.set(INFO_PANEL_SEARCH_PARAM, 'Version')
    newSearchParams.set(SHOW_VERSION_UPLOAD_SEARCH_PARAM, 'true')
    newSearchParams.set(AUTO_PICK_FILE_SEARCH_PARAM, 'true')
    router.push(`${pathname}?${newSearchParams}`)
  }

  return (
    <Button
      onClick={handleUploadNewVersionClick}
      size='sm'
      variant='outline'
      data-testid='compare-versions-btn'
    >
      <Icon
        icon={faArrowRightArrowLeft}
        size='1x'
        color={IconColor.darkPrimary}
      />
      <span className='tw-ml-2 tw-hidden lg:tw-block tw-font-medium tw-text-sm'>
        Compare versions
      </span>
    </Button>
  )
}
